import React, { useState, useRef, useEffect } from "react";
import { Modal, Button, Typography, Tabs } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import CodeEditor from "../common/code-editor";

const { TabPane } = Tabs;

const SchemaConfigDialog = ({
  open,
  onClose,
  currentSchema,
  onSaveConfig,
  onSaveScript,
  prodConfig,
  setProdConfig,
  stagingConfig,
  setStagingConfig,
  details,
  setDetails,
}) => {
  const [schemaTab, setSchemaTab] = useState(0);
  const [envTab, setEnvTab] = useState(0);
  const currentEnvRef = useRef(0);
  const [configCode, setConfigCode] = useState("");
  const [diffConfigCode, setDiffConfigCode] = useState("");

  // Update config codes when dialog opens or environment changes
  useEffect(() => {
    if (open && currentSchema?.type === "config") {
      if (envTab === 0) {
        setConfigCode(prodConfig.latest || "");
        setDiffConfigCode(prodConfig.inUse || "");
      } else {
        setConfigCode(stagingConfig.latest || "");
        setDiffConfigCode(stagingConfig.inUse || "");
      }
    }
  }, [open, currentSchema, envTab, prodConfig, stagingConfig]);

  // Set initial schema tab when details change
  useEffect(() => {
    if (details.length > 0) {
      const inUseIndex = details.findIndex(detail => detail.inUse);
      if (inUseIndex !== -1) {
        setSchemaTab(inUseIndex);
      }
    }
  }, [details]);

  const handleSchemaTabChange = (newValue) => {
    setSchemaTab(newValue);
  };

  const handleEnvTabChange = (newValue) => {
    setEnvTab(newValue);
    currentEnvRef.current = newValue;
    // Update the displayed configs based on the selected environment
    if (newValue === 0) {
      setConfigCode(prodConfig.latest);
      setDiffConfigCode(prodConfig.inUse);
    } else {
      setConfigCode(stagingConfig.latest);
      setDiffConfigCode(stagingConfig.inUse);
    }
  };

  const handleClose = () => {
    setConfigCode("");
    setDiffConfigCode("");
    setSchemaTab(0);
    setEnvTab(0);
    onClose();
  };

  const handleSave = () => {
    onSaveScript();
  };

  if (!currentSchema) return null;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={currentSchema.type === "config" ? "Config" : "Script"}
      width="90%"
      style={{ top: 20 }}
      footer={
        currentSchema.type === "config"
          ? null
          : [
              <Button key="save" type="primary" onClick={handleSave}>
                Save Script
              </Button>,
            ]
      }
      closeIcon={<CloseOutlined />}
    >
        {currentSchema.type === "script" ? (
        <div>
                <Tabs
            activeKey={String(schemaTab)}
                  onChange={handleSchemaTabChange}
                >
                  {details.map((detail, index) => (
              <TabPane
                      key={index}
                tab={
                        detail.inUse
                          ? "Current"
                          : detail.draft
                          ? "Draft"
                          : `Version ${detail.v}`
                      }
              >
                <div style={{ marginTop: 16 }}>
                      <CodeEditor
                        value={detail.schema}
                        editable={true}
                        parseResults={detail.parseResults || []}
                        height="800px"
                        onChange={instance => {
                          const updatedDetails = [...details];
                          updatedDetails[index] = {
                            ...updatedDetails[index],
                            schema: instance.getValue(),
                            isModified: true,
                          };
                          setDetails(updatedDetails);
                        }}
                      />
                </div>
              </TabPane>
            ))}
          </Tabs>
        </div>
        ) : (
        <div>
              <Tabs
            activeKey={String(envTab)}
                onChange={handleEnvTabChange}
            style={{ marginBottom: 16 }}
              >
            <TabPane tab="Production" key="0" />
            <TabPane tab="Staging" key="1" />
              </Tabs>
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                  Latest Configuration
              </Typography.Title>
                <CodeEditor
                  value={configCode}
                  editable={false}
                  height="800px"
                  onChange={instance => {
                    const code = instance.getValue();
                    setConfigCode(code);
                    // Use the ref value which is always up to date
                    if (currentEnvRef.current === 0) {
                      setProdConfig(prev => ({ ...prev, latest: code }));
                    } else {
                      setStagingConfig(prev => ({ ...prev, latest: code }));
                    }
                  }}
                />
            </div>
            <div style={{ flex: 1 }}>
              <Typography.Title level={5} style={{ marginBottom: 8 }}>
                  In-Use Configuration
              </Typography.Title>
                <CodeEditor
                  value={diffConfigCode}
                  editable={false}
                  height="800px"
                />
            </div>
          </div>
        </div>
        )}
    </Modal>
  );
};

export default SchemaConfigDialog;
